import { FC } from 'react';

import { isDeepEqual, isNullish } from 'remeda';
import { toast } from 'sonner';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';

import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';

import { DailyRewardForm, DailyRewardFormValues } from '../DailyRewardForm';

import { getDailyRewardEditFormInitialValues } from './utils';

import { dailyRewardsService } from 'v2/api/dailyRewardsService';
import { rewardsService } from 'v2/api/rewardsService';
import { Loader } from 'v2/components/loader/Loader';
import { DailyRewardModel } from 'v2/types/DailyRewardModel';
import { handleHttpError } from 'v2/utils';

interface DailyRewardEditModalProps {
  isOpened: boolean;
  handleClose: VoidFunction;
  onRevalidate: VoidFunction;
  dailyReward: DailyRewardModel | null;
}

export const DailyRewardEditModal: FC<DailyRewardEditModalProps> = ({
  isOpened,
  handleClose,
  dailyReward,
  onRevalidate,
}) => {
  const { data: initialRewards = [], isLoading: isInitialRewardsLoading } = useSWR(
    isOpened && dailyReward ? ['getRewardsByIds', dailyReward.rewardIds] : null,
    rewardsService.getRewardsByIds
  );

  const { trigger: triggerDeleteRewards, isMutating: isDeletingRewards } = useSWRMutation(
    'deleteRewards',
    (_key, { arg }: { arg: string[] }) => rewardsService.deleteRewards(['deleteRewards', arg])
  );

  const { trigger: triggerCreateRewards, isMutating: isCreatingRewards } = useSWRMutation(
    'createRewards',
    rewardsService.createRewards
  );

  const { trigger: triggerUpdateDailyReward, isMutating: isUpdatingDailyReward } = useSWRMutation(
    'updateDailyReward',
    dailyRewardsService.updateDailyReward
  );

  const handleUpdateDailyReward = async (formValues: DailyRewardFormValues) => {
    if (isNullish(dailyReward)) {
      return;
    }

    try {
      const nextRewardsPayload = formValues.rewards.map(({ type, value }) => ({ type, value }));
      const initialRewardsPayload = initialRewards.map(({ type, value }) => ({ type, value }));

      const isRewardsChanged = !isDeepEqual(nextRewardsPayload, initialRewardsPayload);
      const isDayChanged = formValues.day !== dailyReward.day;
      const isDescriptionChanged = formValues.description !== dailyReward.description;
      const isRolesChanged = !isDeepEqual(formValues.allowedUserRoles, dailyReward.allowedUserRoles);

      if (!isRewardsChanged && !isDayChanged && !isDescriptionChanged && !isRolesChanged) {
        handleClose();
        return;
      }

      let rewardIds = dailyReward.rewardIds;

      if (isRewardsChanged) {
        if (dailyReward.rewardIds.length > 0) {
          await triggerDeleteRewards(dailyReward.rewardIds);
        }

        rewardIds = await triggerCreateRewards(nextRewardsPayload);
      }

      await triggerUpdateDailyReward({
        dailyRewardId: dailyReward.id,
        payload: {
          day: formValues.day,
          description: formValues.description,
          rewardIds,
          allowedUserRoles: formValues.allowedUserRoles,
        },
      });

      toast.success('Daily reward has been successfully updated');
      onRevalidate();
      handleClose();
    } catch (error) {
      await handleHttpError(error);
    }
  };

  if (isNullish(dailyReward)) {
    return null;
  }

  const initialValues = getDailyRewardEditFormInitialValues(dailyReward, initialRewards);
  const isDailyRewardProcessing = isDeletingRewards || isCreatingRewards || isUpdatingDailyReward;

  return (
    <Dialog open={isOpened} onClose={handleClose} maxWidth="sm" scroll="paper" fullWidth>
      <DialogTitle>Edit Daily Reward</DialogTitle>

      <DialogContent
        sx={{
          '&&': {
            pt: 2,
          },
        }}
      >
        {isInitialRewardsLoading ? (
          <Loader />
        ) : (
          <DailyRewardForm
            submitButtonCaption="Edit"
            initialValues={initialValues}
            isSubmitting={isDailyRewardProcessing}
            onSubmit={handleUpdateDailyReward}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};
